<template>
	<div class="review-card">
		<div class="review-card__title">
			<div class="logo">
				<img
					:src="review.logo"
					alt=""
				/>
			</div>
			<div class="customer">
				<div class="name">{{ review.customer.name }}</div>
				<div class="post">{{ review.customer.post }}</div>
			</div>
			<div
				class="contact-link"
				v-if="review.customer.contactLink"
			>
				<a
					:href="review.customer.contactLink"
					target="_blank"
				>
					{{ $t('reviewsPage.contactCustomer') }}
					<i class="icon-arrow-right d-none d-md-inline"></i>
					<i class="icon-chevron-right d-md-none"></i>
				</a>
			</div>
		</div>

		<div
			class="review-card__task"
			v-if="review.task"
		>
			<span class="title">{{ $t('reviewsPage.task') }}:</span>
			<div
				class="html-content"
				v-html="review.task"
			></div>
		</div>

		<div
			class="review-card__coop-duration"
			v-if="review.coopDuration"
		>
			<span class="title">{{ $t('reviewsPage.coopDuration') }}:</span>
			<div
				class="html-content"
				v-html="review.coopDuration"
			></div>
		</div>

		<div
			class="review-card__quote"
			v-if="review.shortQuote"
		>
			{{ review.shortQuote }}
		</div>

		<div class="review-card__content d-none d-md-block">
			<div
				class="html-content"
				v-html="contentExpanded ? review.content : slicedReviewContent"
			></div>
			<div
				class="expand-content-btn"
				:class="{ active: contentExpanded }"
				v-if="slicedReviewContent != review.content"
				@click="toggleExpandContent()"
			>
				{{ contentExpanded ? $t('narrow') : $t('expand') }}
				<i class="icon-chevron-right"></i>
			</div>
		</div>
		<div class="review-card__content_mobile d-md-none">
			<div
				class="html-content"
				v-html="
					contentExpanded ? review.content : slicedReviewContentMobile
				"
			></div>
			<div
				class="expand-content-btn"
				v-if="slicedReviewContentMobile != review.content"
				@click="toggleExpandContent()"
			>
				{{ contentExpanded ? $t('narrow') : $t('expand') }}
			</div>
		</div>

		<div
			class="review-card__images"
			v-if="review.images && review.images.length"
		>
			<swiper
				v-bind="imagesSwiperOptions"
				ref="imagesSwiper"
				class="images-swiper"
			>
				<template #wrapper-start>
					<swiper-slide
						v-for="(img, i) in review.images"
						:key="i"
					>
						<div class="review-img">
							<img
								:src="img"
								alt=""
							/>
						</div>
					</swiper-slide>
				</template>
				<div
					class="images-swiper__pagination swiper-pagination-2 d-md-none"
				></div>
			</swiper>
		</div>

		<div class="review-card__projects">
			<router-link
				v-if="review.caseLink"
				class="btn-primary case-link d-md-none"
				:to="review.caseLink"
			>
				{{ $t('reviewsPage.projectCase') }}
			</router-link>

			<div class="title">{{ $t('reviewsPage.completedProjects') }}:</div>

			<div class="row align-items-center">
				<div class="col-lg">
					<div class="projects">
						<div
							class="projects__item"
							v-if="review.googleplay || review.appstore"
						>
							<div class="project-name">
								{{ $t('reviewsPage.apps') }}:
							</div>
							<a
								class="project-link"
								v-if="review.appstore"
								:href="review.appstore"
								target="_blank"
								><i class="icon-appstore"></i
							></a>
							<a
								class="project-link"
								v-if="review.googleplay"
								:href="review.googleplay"
								target="_blank"
								><i class="icon-googleplay"></i
							></a>
						</div>
						<div
							class="projects__item"
							v-if="review.site"
						>
							<div class="project-name">
								{{ $t('reviewsPage.site') }}:
							</div>
							<a
								class="project-link"
								:href="review.site"
								target="_blank"
								><i class="icon-web-site"></i
							></a>
						</div>
						<div
							class="projects__item"
							v-if="
								review.vk ||
								review.instagram ||
								review.facebook ||
								review.youtube ||
								review.telegram
							"
						>
							<div class="project-name">
								{{ $t('reviewsPage.socials') }}:
							</div>
							<a
								class="project-link"
								v-if="review.vk"
								:href="review.vk"
								target="_blank"
								><i class="icon-vk-circle"></i
							></a>
							<a
								class="project-link"
								v-if="review.instagram"
								:href="review.instagram"
								target="_blank"
								><i class="icon-instagram-circle"></i
							></a>
							<a
								class="project-link"
								v-if="review.facebook"
								:href="review.facebook"
								target="_blank"
								><i class="icon-facebook-circle"></i
							></a>
							<a
								class="project-link"
								v-if="review.youtube"
								:href="review.youtube"
								target="_blank"
								><i class="icon-youtube-circle"></i
							></a>
							<a
								class="project-link"
								v-if="review.telegram"
								:href="review.telegram"
								target="_blank"
								><i class="icon-telegram-circle"></i
							></a>
						</div>
					</div>
				</div>
				<div
					class="col-auto d-none d-md-block"
					v-if="review.caseLink"
				>
					<router-link
						class="btn-outlined has-icon small case-link"
						:to="review.caseLink"
					>
						{{ $t('reviewsPage.projectCase') }}
						<i class="icon-chevron-right"></i>
					</router-link>
				</div>
			</div>
		</div>

		<div
			v-if="slideIndex != undefined"
			class="reviews-swiper__pagination_inside swiper-pagination-1 d-md-none"
		>
			<span
				v-for="i in reviews.length"
				:key="i"
				class="swiper-pagination-bullet"
				:class="{ 'swiper-pagination-bullet-active': i - 1 == slideIndex }"
				@click="$emit('slide-to', i - 1)"
			></span>
		</div>
	</div>
</template>

<script>
import { Swiper, SwiperSlide } from 'swiper/swiper-vue'
import { Pagination, Navigation } from 'swiper/modules'

import 'swiper/swiper.css'
import 'swiper/modules/pagination.css'

export default {
	props: {
		review: {
			type: Object,
			required: true,
		},
		slideIndex: {
			type: Number,
		},
	},

	components: {
		Swiper,
		SwiperSlide,
	},

	data() {
		return {
			contentPreviewLength: 600,
			contentPreviewLengthMobile: 280,
			contentExpanded: false,
			imagesSwiperOptions: {
				modules: [Pagination, Navigation],
				slidesPerView: 2,
				slidesPerGroup: 2,
				spaceBetween: 4,
				pagination: {
					el: '.images-swiper__pagination',
					clickable: true,
				},
				breakpoints: {
					768: { slidesPerView: 'auto', spaceBetween: 12 },
				},
			},
		}
	},

	computed: {
		slicedReviewContent() {
			if (this.review.content.length <= this.contentPreviewLength)
				return this.review.content
			else
				return (
					this.review.content.slice(0, this.contentPreviewLength) + '...'
				)
		},

		slicedReviewContentMobile() {
			if (this.review.content.length <= this.contentPreviewLengthMobile)
				return this.review.content
			else
				return (
					this.review.content.slice(0, this.contentPreviewLengthMobile) +
					'...'
				)
		},

		reviews() {
			return this.$store.getters['reviews/reviewsByLocale']
		},
	},

	methods: {
		toggleExpandContent() {
			this.contentExpanded = !this.contentExpanded
			this.$nextTick(() => this.$emit('updateHeight'))
		},
	},
}
</script>

<style lang="scss">
.review-card .html-content {
	h3 {
		margin-top: 0.8rem;
		margin-bottom: 0.4rem;
	}
	p {
		margin-bottom: 0.4rem;
	}
	:last-child {
		margin-bottom: 0;
	}
	ol li,
	ul li {
		margin-bottom: 4px !important;
	}
}
</style>

<style lang="scss" scoped>
@import '@/sass/_variables';

.review-card {
	background: #ffffff;
	box-shadow: 0px 10px 34px rgba(12, 62, 120, 0.107299);
	border-radius: 10px;
	padding: 48px;
	color: #1d2126;

	&__title {
		display: flex;
		align-items: center;
		.logo {
			width: 100px;
			height: 100px;
			display: flex;
			align-items: center;
			justify-content: center;
			img {
				display: block;
				max-width: 100%;
				user-select: none;
				pointer-events: none;
			}
		}
		.customer {
			margin-left: 24px;
			flex-grow: 1;
			.name {
				font-size: 21px;
				font-weight: bold;
			}
			.post {
				margin-top: 8px;
				font-size: 16px;
				font-weight: 500;
			}
		}
		.contact-link {
			white-space: nowrap;
			margin-left: 24px;
			a {
				text-decoration: none !important;
				font-weight: 600;
				color: $primary;
				transition: 0.3s;
				.icon-arrow-right,
				.icon-chevron-right {
					font-size: 10px;
					margin-left: 12px;
					position: relative;
					left: 0;
					transition: 0.3s;
				}
				&:hover {
					color: darken($primary, 8%);
					.icon-arrow-right {
						left: 10px;
					}
				}
			}
		}
	}

	&__task,
	&__coop-duration {
		margin: 24px 0;
		font-size: 15px;
		.title {
			display: block;
			font-weight: 700;
			margin-bottom: 4px;
		}
	}

	&__quote {
		margin: 32px 0 24px;
		font-style: italic;
		font-size: 16px;
		font-weight: 500;
		&::before {
			content: '« ';
		}
		&::after {
			content: ' »';
		}
		&::before,
		&::after {
			font-weight: bold;
			font-size: 18px;
		}
	}

	&__content {
		margin-top: 16px;
		line-height: 1.5;
		font-weight: 500;
		color: #677a94;
		transition: 0.3s;
		.expand-content-btn {
			margin-top: 24px;
			display: inline-flex;
			align-items: center;
			cursor: pointer;
			color: $primary;
			font-weight: 500;
			font-size: 14px;
			transition: 0.3s;
			.icon-chevron-right {
				margin-left: 12px;
				transform: rotate(90deg);
				transition: 0.2s;
			}
			&:hover {
				color: darken($primary, 8%);
			}
			&.active {
				.icon-chevron-right {
					transform: rotate(-90deg);
				}
			}
		}
	}

	&__images {
		margin-top: 48px;
		margin-bottom: 16px;
		.images-swiper {
			.swiper-slide {
				width: auto;
				display: flex;
				justify-content: center;
				align-items: flex-end;
			}
		}
		.review-img {
			display: inline-flex;
			justify-content: center;
			align-items: flex-end;
			width: 128px;
			position: relative;
			img {
				user-select: none;
				display: block;
				max-width: 100%;
			}
			&::before {
				content: '';
				position: absolute;
				width: 100%;
				height: 22px;
				left: 0;
				bottom: 0;
				background: radial-gradient(
					47.19% 97.92% at 51.21% 100%,
					#000000 0%,
					rgba(0, 0, 0, 0) 100%
				);
				opacity: 0.25;
			}
		}
	}

	&__projects {
		margin-top: 32px;
		padding-top: 24px;
		border-top: 1px solid #f2f2f2;
		.title {
			font-weight: 600;
			font-size: 15px;
			margin-bottom: 6px;
		}
		.projects {
			display: flex;
			flex-wrap: wrap;
			margin: -7px -14px;
			&__item {
				margin: 7px 14px;
				color: $primary;
				display: inline-flex;
				align-items: center;
				.project-name {
					font-weight: 500;
					font-size: 13px;
					margin-right: 12px;
				}
				.project-link {
					line-height: 1;
					color: $primary;
					transition: 0.2s;
					text-decoration: none !important;
					&:hover {
						color: darken($primary, 8%);
					}
					&:not(:last-child) {
						margin-right: 6px;
					}
					i {
						font-size: 24px;
						position: relative;
						&.icon-googleplay {
							top: 1px;
						}
					}
				}
			}
		}
	}
}
@media (max-width: $lg) {
	.review-card {
		padding: 24px;
		&__title {
			.logo {
				width: 78px;
				height: 78px;
			}
			.customer {
				margin-left: 16px;
				.name {
					font-size: 18px;
				}
				.post {
					margin-top: 5px;
					font-size: 14px;
				}
			}
			.contact-link {
				margin-left: 16px;
			}
		}
		&__task,
		&__coop-duration {
			margin: 20px 0;
			font-size: 14px;
		}
		&__quote {
			margin: 28px 0 20px;
			font-size: 15px;
			&::before,
			&::after {
				font-size: 16px;
			}
		}
		&__images {
			margin-top: 36px;
			margin-bottom: 12px;
		}
		&__projects {
			margin-top: 24px;
			.title {
				margin-bottom: 16px;
			}
			.case-link {
				margin-top: 20px;
			}
		}
	}
}
@media (max-width: $md) {
	.review-card {
		background: #1e2047;
		box-shadow: 0px 10px 34px rgba(12, 62, 120, 0.107299);
		border-radius: 14px;
		padding: 24px 16px 30px;
		color: #ffffff;
		&__title {
			display: block;
			.logo {
				border: 1px solid #30393e;
				box-sizing: border-box;
				border-radius: 15px;
				padding: 10px;
				margin: 0 auto 24px;
			}
			.customer {
				margin-left: 0;
				.name {
					font-size: 17px;
				}
				.post {
					font-size: 13px;
				}
			}
			.contact-link {
				margin: 16px 0 0;
				a {
					background: rgba(#020e14, 0.25);
					box-shadow:
						0px 2px 2px rgba(50, 50, 71, 0.06),
						0px 2px 4px rgba(50, 50, 71, 0.06);
					border-radius: 99px;
					font-weight: normal;
					font-size: 13px;
					letter-spacing: -0.4px;
					padding: 6px 12px;
					color: #007aff;
					.icon-chevron-right {
						font-size: 12px;
					}
					&:hover {
						color: #007aff;
						background: rgba(#020e14, 0.5);
					}
				}
			}
		}
		&__task,
		&__coop-duration {
			font-size: 13px;
		}
		&__quote {
			font-size: 13px;
			&::before,
			&::after {
				font-size: 15px;
			}
		}
		&__content_mobile {
			margin-top: 16px;
			letter-spacing: -0.4px;
			color: #fff;
			font-size: 13px;
			line-height: 20px;
			.expand-content-btn {
				margin-top: 16px;
				display: inline-flex;
				align-items: center;
				cursor: pointer;
				color: #007aff;
				font-size: 13px;
				transition: 0.3s;
				&:hover {
					color: lighten(#007aff, 15%);
				}
			}
		}
		&__images {
			margin-top: 16px;
			margin-bottom: 20px;
			.images-swiper {
				width: 248px;
				margin: 0 auto;
				&__pagination {
					margin-top: 18px;
					width: 88%;
					margin-left: 6%;
				}
			}
			.review-img {
				width: 120px;
			}
		}
		&__projects {
			margin-top: 20px;
			padding-top: 0;
			border-top: none;
			.case-link {
				margin: 0 0 38px;
				width: 100%;
				background: #252864;
				font-size: 17px;
				font-weight: 600;
				&::before {
					display: none;
				}
				&:hover {
					background: $primary;
				}
			}
			.title {
				font-size: 13px;
				letter-spacing: -0.08px;
				text-transform: uppercase;
			}
			.projects {
				display: block;
				margin: 0;
				box-sizing: border-box;
				&__item {
					margin: 0;
					color: #fff;
					display: flex;
					align-items: center;
					position: relative;
					width: 100%;
					box-sizing: border-box;
					padding: 8px 0;
					&::before {
						content: '\e900';
						font-family: 'globalcode-icons' !important;
						color: #0166ff;
						font-size: 24px;
						margin-right: 8px;
					}
					&::after {
						content: '';
						display: block;
						position: absolute;
						bottom: 0;
						left: -16px;
						width: calc(100% + 32px);
						background-color: #252864;
						height: 1px;
					}
					.project-name {
						flex-grow: 1;
						font-weight: normal;
						font-size: 14px;
						letter-spacing: -0.4px;
					}
					.project-link {
						color: #0166ff;
						&:hover {
							color: #fff;
						}
					}
				}
			}
		}
		.reviews-swiper__pagination_inside {
			margin-top: 30px;
		}
	}
}
</style>
